import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';

import { OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { QUERY_KEYS } from '.';
import { closeSecondaryPanel } from '../krDrawerSecondaryPanelSlice';
import { useOKRDrawerControlCallbacks } from '../useOKRDrawerControlCallbacks';

const updateKRProgressApi = ({ uuid, ...rest }) => {
  const url = endpoints.rabbitGoals.progress(uuid);

  const { actual, goalProgressLevel, goalClosureStatus } = rest;

  return sendRequest({
    method: 'PUT',
    url,
    body: {
      actual,
      goalProgressLevel,
      goalClosureStatus,
    },
    credentials: 'include',
  });
};

export const useOKRProgressCheckinMutation = (uuid) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { onUpdate } = useOKRDrawerControlCallbacks();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const queryKey = [QUERY_KEYS.KEY_RESULT, uuid];

  return useMutation({
    mutationFn: ({ updates }) => {
      const previousKR = queryClient.getQueryData(queryKey);
      return updateKRProgressApi({ ...previousKR, ...updates });
    },
    onMutate: () => {
      queryClient.cancelQueries(queryKey);

      const previousKR = queryClient.getQueryData(queryKey);
      /* queryClient.setQueryData(queryKey, (previousKR) => {
        return { ...previousKR, ...updates };
      }); */
      return { previousKR };
    },
    onError: (err) => {
      /* queryClient.setQueryData(queryKey, context.previousKR); */
      toast({
        title: err.message || `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} could not be checked in`,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries([QUERY_KEYS.OKR_TOP_HIERARCHY, uuid]);

      // to invalidate okr data in review goal form
      queryClient.invalidateQueries([REACT_QUERY_KEYS.ReviewGoalFormData]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 1000);
      if (typeof onUpdate === 'function') {
        onUpdate();
      }
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(REACT_QUERY_KEYS.GoalsCascadedData);
      // !!!!DON'T SET QUERY DATA AS IT WILL CONTAIN ONLY THE UPDATED FIELDS
      // FOR THE ROW BEING MUTATED AND NOT OTHER ROWS WHICH ARE DEPENDENT ON THIS ROW
      // INSTEAD INVALIDATE THE QUERY SO THAT THE DATA IS FETCHED AGAIN!!!!
      // queryClient.setQueryData(queryKey, data);

      toast({
        title: `The ${
          data?.type === OKR_TYPES.OBJECTIVE
            ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
            : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
        } has been successfully checked in.`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
      dispatch(closeSecondaryPanel());
    },
  });
};
